import * as THREE from "three";

import type { AudioFeatures } from "../audio/types";
import type { AltSceneApi, CameraPersonality } from "./altScenes";
import type { BioclastParams, QualityTier } from "./scenes/bioclast";

const FIELD_X = 54;
const FIELD_Z = 34;
const FLOOR_Y = -16;
const BED_SEGMENTS_X = 128;
const BED_SEGMENTS_Z = 88;
const MAX_BLOBS = 3_800;
const MAX_BUBBLES = 420;
const BUBBLE_RISE = 21;

/**
 * The foam surface is evaluated on the GPU for the bed mesh and on the CPU for
 * blob/bubble placement, so both copies of this noise must stay identical.
 */
const FOAM_FIELD_GLSL = `
  uniform float uTime;
  uniform float uFoamDepth;
  uniform float uSwell;
  uniform float uFieldX;
  uniform float uFieldZ;
  uniform float uFloorY;

  float fxHash(vec2 p) {
    return fract(sin(p.x * 127.1 + p.y * 311.7) * 43758.5453123);
  }

  float fxNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = fxHash(i);
    float b = fxHash(i + vec2(1.0, 0.0));
    float c = fxHash(i + vec2(0.0, 1.0));
    float d = fxHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  float fxCoverage(vec2 p) {
    float r = length(vec2(p.x / uFieldX, p.y / uFieldZ));
    return 1.0 - smoothstep(0.74, 1.05, r);
  }

  float fxSurface(vec2 p) {
    float h = fxNoise(p * 0.042 + vec2(uTime * 0.045, uTime * 0.018));
    h += 0.55 * fxNoise(p * 0.105 + vec2(-uTime * 0.062, uTime * 0.036));
    h += 0.28 * fxNoise(p * 0.215 + vec2(uTime * 0.028, -uTime * 0.078));
    return h / 1.83;
  }

  float foamHeight(vec2 p) {
    float cov = fxCoverage(p);
    float h = fxSurface(p);
    return uFloorY + cov * (uFoamDepth * (0.28 + 0.72 * h) + uSwell * (0.30 + 0.70 * h));
  }

  vec3 foamNormal(vec2 p) {
    float e = 0.75;
    float hx = foamHeight(p + vec2(e, 0.0)) - foamHeight(p - vec2(e, 0.0));
    float hz = foamHeight(p + vec2(0.0, e)) - foamHeight(p - vec2(0.0, e));
    return normalize(vec3(-hx, 2.0 * e, -hz));
  }
`;

const FOAM_SURFACE_FRAGMENT_HEAD = `
  uniform float uSparkle;
  varying vec3 vFoamWorld;
  varying vec3 vFoamNormalW;
`;

/** Wet-foam response: cool crevice shading and a soft grazing sheen. */
const FOAM_SURFACE_FRAGMENT_BODY = `
  vec3 foamNrm = normalize(vFoamNormalW);
  vec3 foamView = normalize(cameraPosition - vFoamWorld);
  float foamFresnel = pow(1.0 - clamp(dot(foamNrm, foamView), 0.0, 1.0), 3.0);
  float crevice = 1.0 - clamp(foamNrm.y, 0.0, 1.0);
  gl_FragColor.rgb *= mix(vec3(1.0), vec3(0.80, 0.89, 1.0), crevice * 0.38);
  gl_FragColor.rgb += vec3(0.78, 0.91, 1.0) * foamFresnel * (0.10 + uSparkle * 0.16);
`;

type FoamUniforms = {
  uTime: THREE.IUniform<number>;
  uFoamDepth: THREE.IUniform<number>;
  uSwell: THREE.IUniform<number>;
  uFieldX: THREE.IUniform<number>;
  uFieldZ: THREE.IUniform<number>;
  uFloorY: THREE.IUniform<number>;
  uSparkle: THREE.IUniform<number>;
};

function hashUnit(value: number): number {
  const result = Math.sin(value * 91.3458 + 17.143) * 47_135.853;
  return result - Math.floor(result);
}

function fract(value: number): number {
  return value - Math.floor(value);
}

function fxHash(x: number, y: number): number {
  return fract(Math.sin(x * 127.1 + y * 311.7) * 43758.5453123);
}

function fxNoise(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = fxHash(ix, iy);
  const b = fxHash(ix + 1, iy);
  const c = fxHash(ix, iy + 1);
  const d = fxHash(ix + 1, iy + 1);
  return (a * (1 - ux) + b * ux) * (1 - uy) + (c * (1 - ux) + d * ux) * uy;
}

/** One irregular lump reused at random orientations reads as foam; a sphere reads as a ball. */
function createFoamLumpGeometry(): THREE.SphereGeometry {
  // Indexed geometry is required here: non-indexed primitives resolve to flat facet normals.
  const geometry = new THREE.SphereGeometry(1, 18, 12);
  const position = geometry.attributes.position as THREE.BufferAttribute;
  for (let index = 0; index < position.count; index++) {
    const x = position.getX(index);
    const y = position.getY(index);
    const z = position.getZ(index);
    const bump =
      0.17 * (fxNoise(x * 2.1 + 7.3, z * 2.1 + 3.9) - 0.5) +
      0.12 * (fxNoise(y * 3.3 + 11.7, x * 3.3 + 5.1) - 0.5) +
      0.07 * (fxNoise(z * 5.2 + 2.4, y * 5.2 + 8.8) - 0.5);
    const scale = 1 + bump;
    position.setXYZ(index, x * scale, y * scale, z * scale);
  }
  geometry.computeVertexNormals();
  return geometry;
}

function countsForTier(tier: QualityTier): { blobs: number; bubbles: number } {
  switch (tier) {
    case "mobile": return { blobs: 1_100, bubbles: 90 };
    case "standard": return { blobs: 2_300, bubbles: 190 };
    case "high": return { blobs: 3_000, bubbles: 280 };
    case "ultra": return { blobs: 3_400, bubbles: 360 };
    case "lab": return { blobs: MAX_BLOBS, bubbles: MAX_BUBBLES };
  }
}

export class FoamPartyScene implements AltSceneApi {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 400);
  readonly backdrop = { r: 4, g: 12, b: 20, alpha: 0.5, blurPx: 38 };
  readonly cameraPersonality: CameraPersonality = "staticParallax";
  beatLevel = 0;

  private readonly root = new THREE.Group();
  private readonly foamUniforms: FoamUniforms = {
    uTime: { value: 0 },
    uFoamDepth: { value: 6.5 },
    uSwell: { value: 0 },
    uFieldX: { value: FIELD_X },
    uFieldZ: { value: FIELD_Z },
    uFloorY: { value: FLOOR_Y },
    uSparkle: { value: 1 },
  };

  private readonly bedGeometry: THREE.PlaneGeometry;
  private readonly bedMaterial = new THREE.MeshStandardMaterial({
    color: 0xf7fcff,
    roughness: 0.80,
    metalness: 0,
  });
  private readonly bed: THREE.Mesh;

  private readonly blobGeometry = createFoamLumpGeometry();
  private readonly blobMaterial = new THREE.MeshStandardMaterial({
    color: 0xfdfeff,
    roughness: 0.76,
    metalness: 0,
  });
  private readonly blobs: THREE.InstancedMesh;

  private readonly bubbleGeometry = new THREE.SphereGeometry(1, 20, 14);
  private readonly bubbleMaterial = new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    uniforms: {
      uTime: { value: 0 },
      uOpacity: { value: 0.92 },
      uTreble: { value: 0 },
    },
    vertexShader: `
      precision highp float;
      varying vec3 vWorldNormal;
      varying vec3 vWorldPosition;
      varying vec3 vTint;
      varying float vPhase;

      void main() {
        mat4 instanceWorld = modelMatrix * instanceMatrix;
        vec4 worldPosition = instanceWorld * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        vWorldNormal = normalize(mat3(instanceWorld) * normal);
        vTint = instanceColor;
        vPhase = fract(instanceMatrix[3].x * 0.071 + instanceMatrix[3].z * 0.113);
        gl_Position = projectionMatrix * viewMatrix * worldPosition;
      }
    `,
    fragmentShader: `
      precision highp float;
      uniform float uTime;
      uniform float uOpacity;
      uniform float uTreble;
      varying vec3 vWorldNormal;
      varying vec3 vWorldPosition;
      varying vec3 vTint;
      varying float vPhase;

      const float TAU = 6.28318530718;

      void main() {
        vec3 normal = normalize(vWorldNormal);
        vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
        float ndv = clamp(abs(dot(normal, viewDirection)), 0.0, 1.0);
        float fresnel = pow(1.0 - ndv, 2.2);

        // Film thickness drifts per bubble, so the interference ring shifts hue as it floats.
        float thickness = 0.62 + 0.38 * sin(vPhase * TAU + uTime * 0.3);
        float band = (1.0 - ndv) * 6.5 * thickness + vPhase * 3.0 + uTreble * 0.6;
        vec3 film = 0.5 + 0.5 * cos(band + vec3(0.0, 2.094, 4.188));
        film = mix(vec3(1.0), film, 0.82);
        film = mix(film, vTint, 0.20);

        vec3 keyDirection = normalize(vec3(-0.42, 0.78, 0.46));
        vec3 reflectedView = reflect(-viewDirection, normal);
        float highlight = pow(max(0.0, dot(reflectedView, keyDirection)), 48.0);

        // A soap bubble is mostly what it reflects: bright foam below, dark air above,
        // and the venue's coloured wash from the sides.
        float downward = clamp(-reflectedView.y * 0.5 + 0.5, 0.0, 1.0);
        vec3 environment = mix(vec3(0.015, 0.03, 0.06), vec3(0.95, 0.98, 1.0), pow(downward, 1.5));
        environment += vec3(0.25, 0.75, 1.0) *
          pow(max(0.0, dot(reflectedView, normalize(vec3(-1.0, 0.06, 0.34)))), 6.0) * 0.85;
        environment += vec3(1.0, 0.30, 0.66) *
          pow(max(0.0, dot(reflectedView, normalize(vec3(1.0, 0.03, 0.22)))), 6.0) * 0.75;

        float alpha = (0.13 + fresnel * 0.74 + highlight * 0.85) * uOpacity;
        if (alpha < 0.006) discard;

        vec3 color = environment * film * (1.15 + fresnel * 1.7);
        color += vec3(1.0) * highlight * 3.2;
        gl_FragColor = vec4(min(color, vec3(3.0)), clamp(alpha, 0.0, 0.92));
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
  private readonly bubbles: THREE.InstancedMesh;

  private readonly floorMaterial = new THREE.MeshStandardMaterial({
    color: 0x03070c,
    roughness: 0.5,
    metalness: 0.1,
  });
  private readonly floor: THREE.Mesh;

  private readonly skyMaterial = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      uSkyTop: { value: new THREE.Color(0x01030a) },
      uSkyHorizon: { value: new THREE.Color(0x0d2740) },
    },
    vertexShader: `
      varying vec3 vSkyPosition;
      void main() {
        vSkyPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 uSkyTop;
      uniform vec3 uSkyHorizon;
      varying vec3 vSkyPosition;
      void main() {
        float h = clamp(vSkyPosition.y / 240.0 * 0.5 + 0.5, 0.0, 1.0);
        vec3 sky = mix(uSkyHorizon, uSkyTop, pow(h, 0.55));
        gl_FragColor = vec4(sky, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
  private readonly sky: THREE.Mesh;

  private readonly hemisphereLight = new THREE.HemisphereLight(0xf2fcff, 0x101c26, 3.1);
  private readonly keyLight = new THREE.DirectionalLight(0xffffff, 3.0);
  private readonly cyanLight = new THREE.PointLight(0x48ddff, 40, 130, 1.5);
  private readonly magentaLight = new THREE.PointLight(0xff4ca8, 34, 130, 1.5);

  private readonly dummy = new THREE.Object3D();
  private readonly color = new THREE.Color();

  private readonly blobX = new Float32Array(MAX_BLOBS);
  private readonly blobZ = new Float32Array(MAX_BLOBS);
  private readonly blobRadius = new Float32Array(MAX_BLOBS);
  private readonly blobLift = new Float32Array(MAX_BLOBS);
  private readonly blobSquash = new Float32Array(MAX_BLOBS);
  private readonly blobStretch = new Float32Array(MAX_BLOBS);
  private readonly blobPhase = new Float32Array(MAX_BLOBS);
  private readonly blobSpin = new Float32Array(MAX_BLOBS);

  private readonly clumpPhase = new Float32Array(MAX_BLOBS);
  private readonly clumpSpeed = new Float32Array(MAX_BLOBS);
  private readonly clumpDriftX = new Float32Array(MAX_BLOBS);
  private readonly clumpDriftZ = new Float32Array(MAX_BLOBS);

  private readonly bubbleX = new Float32Array(MAX_BUBBLES);
  private readonly bubbleZ = new Float32Array(MAX_BUBBLES);
  private readonly bubblePhase = new Float32Array(MAX_BUBBLES);
  private readonly bubbleSpeed = new Float32Array(MAX_BUBBLES);
  private readonly bubbleRadius = new Float32Array(MAX_BUBBLES);
  private readonly bubbleHue = new Float32Array(MAX_BUBBLES);
  private readonly bubbleGate = new Float32Array(MAX_BUBBLES);

  private time = 0;
  private visualTime: number | null = null;
  private foamLevel = 0.7;
  private clumpEnergy = 0;
  private activeBlobs = 0;
  private activeClumps = 0;
  private activeBubbles = 0;

  constructor(width: number, height: number) {
    this.camera.position.set(0, -3.2, 42);
    this.camera.lookAt(0, -9, 0);
    this.setSize(width, height);

    this.scene.fog = new THREE.FogExp2(0x03070e, 0.0135);
    this.keyLight.position.set(-14, 26, 26);
    this.cyanLight.position.set(-30, -6, 20);
    this.magentaLight.position.set(30, -8, 6);
    this.scene.add(this.hemisphereLight, this.keyLight, this.cyanLight, this.magentaLight);

    this.bedGeometry = new THREE.PlaneGeometry(
      FIELD_X * 2,
      FIELD_Z * 2,
      BED_SEGMENTS_X,
      BED_SEGMENTS_Z,
    );
    this.bedGeometry.rotateX(-Math.PI * 0.5);
    this.applyFoamSurface(this.bedMaterial, true);
    this.bed = new THREE.Mesh(this.bedGeometry, this.bedMaterial);
    this.bed.frustumCulled = false;
    this.bed.renderOrder = 1;

    this.applyFoamSurface(this.blobMaterial, false);
    this.blobs = new THREE.InstancedMesh(this.blobGeometry, this.blobMaterial, MAX_BLOBS);
    this.blobs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.blobs.frustumCulled = false;
    this.blobs.renderOrder = 2;

    this.bubbles = new THREE.InstancedMesh(this.bubbleGeometry, this.bubbleMaterial, MAX_BUBBLES);
    this.bubbles.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.bubbles.frustumCulled = false;
    this.bubbles.renderOrder = 4;

    this.floor = new THREE.Mesh(new THREE.PlaneGeometry(FIELD_X * 3, FIELD_Z * 3), this.floorMaterial);
    this.floor.rotation.x = -Math.PI * 0.5;
    this.floor.position.y = FLOOR_Y - 0.35;

    this.sky = new THREE.Mesh(new THREE.SphereGeometry(240, 24, 16), this.skyMaterial);
    this.sky.frustumCulled = false;
    this.sky.renderOrder = 0;
    this.scene.add(this.sky);

    this.root.add(this.floor, this.bed, this.blobs, this.bubbles);
    this.scene.add(this.root);

    this.seedBlobs();
    this.seedBubbles();
  }

  /** Shares the foam field and glitter response between the bed and the blobs. */
  private applyFoamSurface(material: THREE.MeshStandardMaterial, displaced: boolean): void {
    material.customProgramCacheKey = (): string => (displaced ? "pfx-foam-bed" : "pfx-foam-blob");
    material.onBeforeCompile = (shader): void => {
      Object.assign(shader.uniforms, this.foamUniforms);

      const vertexHead = `
        varying vec3 vFoamWorld;
        varying vec3 vFoamNormalW;
        ${displaced ? FOAM_FIELD_GLSL : ""}
      `;
      shader.vertexShader = vertexHead + shader.vertexShader;

      if (displaced) {
        shader.vertexShader = shader.vertexShader.replace(
          "#include <beginnormal_vertex>",
          "vec3 objectNormal = foamNormal(position.xz);",
        );
        shader.vertexShader = shader.vertexShader.replace(
          "#include <begin_vertex>",
          `
          vec3 transformed = vec3(position.x, foamHeight(position.xz), position.z);
          vFoamWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
          vFoamNormalW = normalize(mat3(modelMatrix) * objectNormal);
          `,
        );
      } else {
        shader.vertexShader = shader.vertexShader.replace(
          "#include <begin_vertex>",
          `
          #include <begin_vertex>
          #ifdef USE_INSTANCING
            vFoamWorld = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
            vFoamNormalW = normalize(mat3(modelMatrix) * mat3(instanceMatrix) * objectNormal);
          #else
            vFoamWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
            vFoamNormalW = normalize(mat3(modelMatrix) * objectNormal);
          #endif
          `,
        );
      }

      shader.fragmentShader = FOAM_SURFACE_FRAGMENT_HEAD + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <dithering_fragment>",
        `${FOAM_SURFACE_FRAGMENT_BODY}
        #include <dithering_fragment>`,
      );
    };
  }

  private seedBlobs(): void {
    for (let index = 0; index < MAX_BLOBS; index++) {
      // Foam arrives in clumps, so blobs are drawn around shared cluster centres
      // and stacked vertically instead of scattered evenly across the floor.
      const clusterId = Math.floor(hashUnit(index + 613) * 320);
      const clusterAngle = hashUnit(clusterId + 11) * Math.PI * 2;
      const clusterRadial = Math.sqrt(hashUnit(clusterId + 29));
      const clusterScale = hashUnit(clusterId + 47);
      const spread = 0.7 + clusterScale * 2.2;

      this.blobX[index] =
        Math.cos(clusterAngle) * clusterRadial * FIELD_X * 0.95 +
        (hashUnit(index + 71) - 0.5) * spread * 2;
      this.blobZ[index] =
        Math.sin(clusterAngle) * clusterRadial * FIELD_Z * 0.95 +
        (hashUnit(index + 97) - 0.5) * spread * 2;

      const tier = hashUnit(index + 263);
      let radius: number;
      if (tier < 0.12) radius = 0.70 + hashUnit(index + 281) * 0.85 * (0.5 + clusterScale);
      else if (tier < 0.40) radius = 0.34 + hashUnit(index + 293) * 0.40;
      else radius = 0.13 + hashUnit(index + 307) * 0.20;

      this.blobRadius[index] = radius;
      this.blobLift[index] =
        Math.pow(hashUnit(index + 733), 1.7) * (0.4 + clusterScale * 2.6);
      this.blobSquash[index] = 0.60 + hashUnit(index + 331) * 0.34;
      this.blobStretch[index] = 0.78 + hashUnit(index + 337) * 0.46;
      this.blobPhase[index] = hashUnit(index + 353) * Math.PI * 2;
      this.blobSpin[index] = (hashUnit(index + 379) - 0.5) * 0.8;

      this.clumpPhase[index] = hashUnit(index + 401);
      this.clumpSpeed[index] = 0.10 + hashUnit(index + 419) * 0.16;
      this.clumpDriftX[index] = (hashUnit(index + 433) - 0.5) * 16;
      this.clumpDriftZ[index] = (hashUnit(index + 457) - 0.5) * 9;
    }
  }

  private seedBubbles(): void {
    for (let index = 0; index < MAX_BUBBLES; index++) {
      const angle = hashUnit(index + 11) * Math.PI * 2;
      const radial = Math.sqrt(hashUnit(index + 43));
      this.bubbleX[index] = Math.cos(angle) * radial * FIELD_X * 0.86;
      this.bubbleZ[index] = Math.sin(angle) * radial * FIELD_Z * 0.86;
      this.bubblePhase[index] = hashUnit(index + 79);
      this.bubbleSpeed[index] = 0.030 + hashUnit(index + 101) * 0.055;
      this.bubbleRadius[index] = 0.26 + Math.pow(hashUnit(index + 131), 1.7) * 0.95;
      this.bubbleHue[index] = hashUnit(index + 167);
      this.bubbleGate[index] = hashUnit(index + 191) * 0.85;
    }
  }

  private foamHeightAt(x: number, z: number): number {
    const time = this.foamUniforms.uTime.value;
    const radial = Math.hypot(x / FIELD_X, z / FIELD_Z);
    const coverage = 1 - THREE.MathUtils.smoothstep(radial, 0.74, 1.05);
    let surface = fxNoise(x * 0.042 + time * 0.045, z * 0.042 + time * 0.018);
    surface += 0.55 * fxNoise(x * 0.105 - time * 0.062, z * 0.105 + time * 0.036);
    surface += 0.28 * fxNoise(x * 0.215 + time * 0.028, z * 0.215 - time * 0.078);
    surface /= 1.83;
    const depth = this.foamUniforms.uFoamDepth.value;
    const swell = this.foamUniforms.uSwell.value;
    return FLOOR_Y + coverage * (depth * (0.28 + 0.72 * surface) + swell * (0.30 + 0.70 * surface));
  }

  setSize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  setVisualTime(timeSeconds: number | null): void {
    this.visualTime = timeSeconds;
  }

  setOutputSpread(spreadX: number): void {
    this.root.scale.set(THREE.MathUtils.clamp(spreadX, 1, 3.5), 1, 1);
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.beatLevel = features.beat > this.beatLevel
      ? features.beat
      : this.beatLevel * Math.exp(-dt * 4.6);
    const level = THREE.MathUtils.clamp(
      Math.max(features.level, (features.bass + features.mid + features.treble) / 3),
      0,
      1,
    );
    if (this.visualTime === null) {
      this.time += dt * (0.32 + params.flowSpeed * 0.5 + level * 0.22);
    } else {
      this.time = this.visualTime * (0.32 + params.flowSpeed * 0.5);
    }

    const bass = THREE.MathUtils.clamp(features.bass * params.bassGravity, 0, 1.5);
    const mid = THREE.MathUtils.clamp(features.mid * params.midCurl, 0, 1.5);
    const treble = THREE.MathUtils.clamp(features.treble * params.trebleShimmer, 0, 1.5);

    const densityScale = THREE.MathUtils.clamp(params.mathParticleCount / 34_000, 0.55, 1.4);
    const tierCounts = countsForTier(params.qualityTier);
    const blobBudget = Math.min(MAX_BLOBS, Math.round(tierCounts.blobs * densityScale));
    this.activeClumps = Math.min(90, Math.round(blobBudget * 0.08));
    this.activeBlobs = blobBudget - this.activeClumps;
    this.activeBubbles = Math.min(MAX_BUBBLES, Math.round(tierCounts.bubbles * densityScale));
    this.blobs.count = blobBudget;
    this.bubbles.count = this.activeBubbles;

    const foamTarget = THREE.MathUtils.clamp(
      0.70 + bass * 0.16 + level * 0.08 + this.beatLevel * 0.10,
      0.6,
      1.0,
    );
    this.foamLevel += (foamTarget - this.foamLevel) * (1 - Math.exp(-dt * 2.2));
    this.clumpEnergy = Math.max(this.beatLevel, this.clumpEnergy * Math.exp(-dt * 1.1));

    const depthGain = THREE.MathUtils.clamp(params.mathAmplitude, 0.4, 1.6);
    this.foamUniforms.uTime.value = this.time;
    this.foamUniforms.uFoamDepth.value = 8.6 * this.foamLevel * depthGain;
    this.foamUniforms.uSwell.value = this.beatLevel * (0.8 + bass * 1.5);
    this.foamUniforms.uSparkle.value = 0.45 + treble * 0.7 + level * 0.2;

    this.updateBlobs(params, mid);
    this.updateClumps(params);
    this.updateBubbles(params, level, bass, mid, treble);
    this.updateLighting(params, level, bass, treble);
  }

  private updateBlobs(params: BioclastParams, mid: number): void {
    const sizeGain = THREE.MathUtils.clamp(params.mathPointSize, 0.6, 1.5);
    const breathe = 0.04 + mid * 0.05;
    for (let index = 0; index < this.activeBlobs; index++) {
      const x = this.blobX[index];
      const z = this.blobZ[index];
      const surface = this.foamHeightAt(x, z);
      const phase = this.blobPhase[index];
      const radius = this.blobRadius[index] * sizeGain * (1 + Math.sin(this.time * 0.9 + phase) * breathe);
      const squash = this.blobSquash[index];

      // Blobs sit partly inside the bed so the mass reads as one body, not loose spheres.
      this.dummy.position.set(x, surface + this.blobLift[index] + radius * squash * 0.35, z);
      this.dummy.rotation.set(phase * 0.4, phase + this.time * this.blobSpin[index] * 0.1, phase * 0.7);
      this.dummy.scale.set(radius, radius * squash, radius * this.blobStretch[index]);
      this.dummy.updateMatrix();
      this.blobs.setMatrixAt(index, this.dummy.matrix);
    }
    this.blobs.instanceMatrix.needsUpdate = true;
  }

  /** Foam torn off the surface and thrown into the air on strong beats. */
  private updateClumps(params: BioclastParams): void {
    const sizeGain = THREE.MathUtils.clamp(params.mathPointSize, 0.6, 1.5);
    const launch = 3.5 + this.clumpEnergy * 13;
    for (let slot = 0; slot < this.activeClumps; slot++) {
      const index = this.activeBlobs + slot;
      const age = fract(this.clumpPhase[index] + this.time * this.clumpSpeed[index]);
      const arc = Math.sin(Math.PI * age);
      const x = this.blobX[index] + this.clumpDriftX[index] * age;
      const z = this.blobZ[index] + this.clumpDriftZ[index] * age;
      const surface = this.foamHeightAt(x, z);
      const radius = this.blobRadius[index] * sizeGain * 0.8 *
        THREE.MathUtils.clamp(this.clumpEnergy * 1.6, 0, 1) *
        Math.sin(Math.PI * Math.min(1, age * 1.4));

      this.dummy.position.set(x, surface + arc * launch, z);
      this.dummy.rotation.set(age * 6.2, this.blobPhase[index] + age * 4.1, age * 3.4);
      this.dummy.scale.setScalar(Math.max(0.001, radius));
      this.dummy.updateMatrix();
      this.blobs.setMatrixAt(index, this.dummy.matrix);
    }
  }

  private updateBubbles(
    params: BioclastParams,
    level: number,
    bass: number,
    mid: number,
    treble: number,
  ): void {
    const sizeGain = THREE.MathUtils.clamp(params.mathPointSize, 0.65, 1.45);
    const hueBase = ((params.paletteHue % 360) + 360) % 360 / 360;
    // Louder music releases more bubbles out of the foam.
    const emission = THREE.MathUtils.clamp(0.30 + level * 0.55 + this.beatLevel * 0.40, 0, 1.15);
    const riseGain = 0.72 + level * 0.55;
    const sway = 0.6 + mid * 1.1;

    for (let index = 0; index < this.activeBubbles; index++) {
      const drift = this.time * 0.5 + index * 0.73;
      const x = this.bubbleX[index] + Math.sin(drift) * sway;
      const z = this.bubbleZ[index] + Math.cos(drift * 0.78) * sway * 0.7;
      const age = fract(this.bubblePhase[index] + this.time * this.bubbleSpeed[index] * riseGain);

      const released = emission > this.bubbleGate[index] ? 1 : 0;
      const birth = THREE.MathUtils.smoothstep(age, 0.0, 0.08);
      const death = 1 - THREE.MathUtils.smoothstep(age, 0.86, 1.0);
      const envelope = released * birth * death;

      // Bubbles are born at the foam surface and lift away from it.
      const surface = this.foamHeightAt(x, z);
      const pulse = 1 + treble * 0.14 * Math.sin(this.time * 2.6 + index * 1.7);
      const radius = this.bubbleRadius[index] * sizeGain * pulse * envelope;

      this.dummy.position.set(
        x,
        surface + age * BUBBLE_RISE + this.beatLevel * (1 - age) * 2.2,
        z,
      );
      this.dummy.rotation.set(
        this.time * (0.16 + this.bubbleSpeed[index]) + index,
        this.time * (0.11 + this.bubbleSpeed[index] * 0.7) + this.bubblePhase[index] * 6,
        this.time * 0.08 + index * 0.31,
      );
      this.dummy.scale.setScalar(Math.max(0.0005, radius));
      this.dummy.updateMatrix();
      this.bubbles.setMatrixAt(index, this.dummy.matrix);

      const hueTravel = params.mathColorFlow * (this.time * 0.015 + bass * 0.04);
      const hue = fract(hueBase + this.bubbleHue[index] * params.mathColorSpread + hueTravel);
      this.color.setHSL(hue, 0.35 * params.saturation, 0.9);
      this.bubbles.setColorAt(index, this.color);
    }
    this.bubbles.instanceMatrix.needsUpdate = true;
    if (this.bubbles.instanceColor) this.bubbles.instanceColor.needsUpdate = true;
  }

  private updateLighting(
    params: BioclastParams,
    level: number,
    bass: number,
    treble: number,
  ): void {
    const hue = ((params.paletteHue % 360) + 360) % 360;
    this.color.setHSL(hue / 360, 0.72 * params.saturation, 0.66);
    this.cyanLight.color.copy(this.color).lerp(new THREE.Color(0x59e8ff), 0.5);
    this.magentaLight.color.setHSL(fract(hue / 360 + 0.36), 0.82 * params.saturation, 0.6);
    this.cyanLight.intensity = 30 + level * 34 + bass * 18;
    this.magentaLight.intensity = 24 + level * 30 + treble * 18;
    this.keyLight.intensity = 2.6 + level * 0.9;
    this.hemisphereLight.intensity = 2.8 + level * 0.6;

    this.bubbleMaterial.uniforms.uTime.value = this.time;
    this.bubbleMaterial.uniforms.uTreble.value = treble;
    this.bubbleMaterial.uniforms.uOpacity.value = THREE.MathUtils.clamp(params.opacity, 0.55, 1);

    this.backdrop.r = Math.round(4 + this.magentaLight.color.r * 18 + bass * 9);
    this.backdrop.g = Math.round(10 + this.cyanLight.color.g * 24 + level * 8);
    this.backdrop.b = Math.round(18 + this.cyanLight.color.b * 32 + treble * 10);
    this.backdrop.alpha = THREE.MathUtils.clamp(0.40 + params.atmosphere * 0.24 + level * 0.12, 0, 0.9);
    this.backdrop.blurPx = 34 + params.atmosphere * 18 + level * 8;

    const horizon = this.skyMaterial.uniforms.uSkyHorizon.value as THREE.Color;
    horizon.setHSL(fract(hue / 360 + 0.02), 0.55 * params.saturation, 0.09 + level * 0.05);
    // Match the floor to the horizon so gaps past the foam edge do not read as holes.
    this.floorMaterial.color.copy(horizon).multiplyScalar(0.85);
  }

  getDiagnostics(): { backend: string; particleCount: number; look: string } {
    return {
      backend: "webgl-foam-surface",
      particleCount: this.activeBlobs + this.activeClumps + this.activeBubbles,
      look: "foam-party-geometry",
    };
  }

  dispose(): void {
    this.bedGeometry.dispose();
    this.bedMaterial.dispose();
    this.blobGeometry.dispose();
    this.blobMaterial.dispose();
    this.bubbleGeometry.dispose();
    this.bubbleMaterial.dispose();
    this.floor.geometry.dispose();
    this.floorMaterial.dispose();
    this.sky.geometry.dispose();
    this.skyMaterial.dispose();
    this.scene.clear();
  }
}
